import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import app from 'flarum/forum/app';

/**
 * AskQuestionDetailModal — 提问详情弹窗
 *
 * 全屏 90% 宽度，无标题。
 * 显示：选中文字、问题，如有权限显示 AI/导师回答。
 * 非本人/未购买时展示购买锁定 UI。
 */
export default class AskQuestionDetailModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);
    /** @type {import('flarum/common/Model').default} AskQuestion model */
    this.q = this.attrs.question;
    this.purchasing = false;
    this.purchaseError = '';
  }

  className() {
    return 'AskQuestionDetailModal'; // 不用 Modal--small，用 CSS 控制 90% 宽
  }

  title() {
    return ''; // 不需要标题
  }

  /** 当前用户是否是提问者 */
  get isOwner() {
    return this.q.attribute('userId') === app.session.user?.id();
  }

  /** 是否有权看答案 */
  get canViewAnswer() {
    return this.q.attribute('canViewAnswer');
  }

  /** 是否需要购买 */
  get needPurchase() {
    return this.q.attribute('needPurchase');
  }

  /** 购买价格 */
  get viewPrice() {
    return this.q.attribute('viewPrice') || 0;
  }

  /** 购买答案 */
  purchase() {
    this.purchasing = true;
    this.purchaseError = '';
    m.redraw();

    app
      .request({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/taro/ask-questions/' + this.q.id() + '/purchase',
      })
      .then(() => {
        // 购买成功 → 重新拉取完整问题数据（答案 now available）
        return app.request({
          method: 'GET',
          url: app.forum.attribute('apiUrl') + '/taro/ask-questions/' + this.q.id(),
        });
      })
      .then((data) => {
        this.purchasing = false;
        // pushPayload 更新 store 中的模型
        const updated = app.store.pushPayload(data);
        this.q = Array.isArray(updated) ? updated[0] : updated;
        m.redraw();
      })
      .catch((err) => {
        this.purchasing = false;
        // 尝试提取后端返回的错误详情
        let detail = '购买失败';
        try {
          const body = JSON.parse(err.response);
          if (body?.errors?.[0]?.detail) detail = body.errors[0].detail;
          else if (body?.errors?.[0]?.title) detail = body.errors[0].title;
        } catch (_) {}
        this.purchaseError = detail;
        m.redraw();
      });
  }

  content() {
    const a = (key) => this.q.attribute(key) || '';
    const highlightedText = a('highlightedText');
    const question = a('question');
    const needAi = this.q.attribute('needAi');
    const needMentor = this.q.attribute('needMentor');
    const aiAnswer = a('aiAnswer');
    const mentorAnswer = a('answer');
    const aiAnswered = needAi && !!aiAnswer;
    const mentorAnswered = needMentor && !!mentorAnswer;
    const hasAnswer = aiAnswered || mentorAnswered;
    const canView = this.canViewAnswer;
    const showPurchase = this.needPurchase && !canView && hasAnswer && !this.isOwner;

    return (
      <div className="Modal-body AskQuestionDetailModal-body">
        {/* 选中文字 */}
        {highlightedText && (
          <div className="AskQuestionDetailModal-section">
            <div className="AskQuestionDetailModal-sectionLabel">
              <i className="fas fa-quote-left"></i> 选中文字
            </div>
            <div className="AskQuestionDetailModal-highlighted">{highlightedText}</div>
          </div>
        )}

        {/* 问题 */}
        <div className="AskQuestionDetailModal-section">
          <div className="AskQuestionDetailModal-sectionLabel">
            <i className="fas fa-question-circle"></i> 问题
          </div>
          <div className="AskQuestionDetailModal-question">{question}</div>
        </div>

        {/* 购买锁定区 */}
        {showPurchase && (
          <div className="AskQuestionDetailModal-purchase">
            <i className="fas fa-lock AskQuestionDetailModal-purchaseIcon"></i>
            <div className="AskQuestionDetailModal-purchaseInfo">
              <p className="AskQuestionDetailModal-purchaseText">
                查看此提问的解答需要 <strong>{this.viewPrice}</strong> 星币
              </p>
              {this.purchaseError && (
                <p className="AskQuestionDetailModal-purchaseError">{this.purchaseError}</p>
              )}
              <Button
                className="Button Button--primary"
                loading={this.purchasing}
                disabled={this.purchasing}
                onclick={() => this.purchase()}
              >
                <i className="fas fa-shopping-cart"></i>
                {' '}购买答案 ({this.viewPrice} 星币)
              </Button>
            </div>
          </div>
        )}

        {/* AI 回复（有权限时显示） */}
        {canView && aiAnswered && (
          <div className="AskQuestionDetailModal-section AskQuestionDetailModal-section--ai">
            <div className="AskQuestionDetailModal-sectionLabel">
              <span className="AskQuestionDetailModal-tag AskQuestionDetailModal-tag--ai">AI</span>
              AI 回复
            </div>
            <div className="AskQuestionDetailModal-answer">{aiAnswer}</div>
          </div>
        )}

        {/* 导师回复（有权限时显示） */}
        {canView && mentorAnswered && (
          <div className="AskQuestionDetailModal-section AskQuestionDetailModal-section--mentor">
            <div className="AskQuestionDetailModal-sectionLabel">
              <span className="AskQuestionDetailModal-tag AskQuestionDetailModal-tag--mentor">师</span>
              导师回复
            </div>
            <div className="AskQuestionDetailModal-answer">{mentorAnswer}</div>
          </div>
        )}

        {/* 等待中（没有答案且没有购买锁，说明还没答） */}
        {!hasAnswer && (needAi || needMentor) && !showPurchase && (
          <div className="AskQuestionDetailModal-section AskQuestionDetailModal-section--waiting">
            <div className="AskQuestionDetailModal-sectionLabel">
              <i className="fas fa-hourglass-half"></i> 等待回答中…
            </div>
          </div>
        )}

        {/* 兜底 */}
        {!needAi && !needMentor && (
          <div className="AskQuestionDetailModal-section AskQuestionDetailModal-section--waiting">
            <div className="AskQuestionDetailModal-sectionLabel">
              <i className="fas fa-info-circle"></i> 未选择答疑方式
            </div>
          </div>
        )}
      </div>
    );
  }
}
